// Localização: ./src/components/AIAssistant/ChatInterface.jsx
// Interface de chat do assistente de IA

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAIAssistant } from '../../hooks/useAIAssistant';
import MessageBubble from './MessageBubble';
import { autocompletePacientesUsuarios } from '../../api/api';
import { getPacienteFotoUrl, getUsuarioFotoUrl } from '../../utils/imageUtils';
import { History, Sparkles } from 'lucide-react';

/**
 * Componente de interface de chat
 * @param {string} initialMessage - Mensagem inicial para enviar automaticamente
 * @param {object} selectedConversation - Conversa selecionada para carregar
 * @param {function} onConversationLoaded - Callback quando conversa é carregada
 * @param {function} onNewConversation - Callback quando nova conversa é iniciada
 * @param {function} onOpenHistory - Callback para abrir o histórico de conversas
 */
const ChatInterface = ({ initialMessage = null, selectedConversation = null, onConversationLoaded = null, onNewConversation = null, onOpenHistory = null }) => {
    const { messages, isLoading, sendMessage, clearChat, loadConversation } = useAIAssistant();
    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const [hasSentInitial, setHasSentInitial] = useState(false);
    const [hasLoadedConversation, setHasLoadedConversation] = useState(false);
    const lastLoadedSessionId = useRef(null);
    
    // Estados para autocomplete
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [cursorPosition, setCursorPosition] = useState(0);
    const suggestionsRef = useRef(null);
    const debounceTimer = useRef(null);
    
    // Scroll automático para a última mensagem
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    useEffect(() => {
        scrollToBottom();
    }, [messages]);
    
    // Foca no input quando o componente monta
    useEffect(() => {
        inputRef.current?.focus();
    }, []);
    
    // Foca no input automaticamente quando a IA termina de responder
    useEffect(() => {
        if (!isLoading && messages.length > 0) {
            // Pequeno delay para garantir que a mensagem foi renderizada
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isLoading, messages.length]);
    
    // Carrega conversa selecionada
    useEffect(() => {
        if (selectedConversation) {
            const sessionId = selectedConversation.session_id || selectedConversation.id;
            
            // Só carregar se for uma conversa diferente da última carregada
            if (sessionId && lastLoadedSessionId.current !== sessionId) {
                setHasLoadedConversation(false);
                lastLoadedSessionId.current = sessionId;
                
                // Carregar a conversa
                loadConversation(selectedConversation).then(() => {
                    setHasLoadedConversation(true);
                    if (onConversationLoaded) {
                        onConversationLoaded();
                    }
                }).catch((error) => {
                    console.error('Erro ao carregar conversa:', error);
                    setHasLoadedConversation(true); // Marcar como carregado mesmo em caso de erro
                    if (onConversationLoaded) {
                        onConversationLoaded();
                    }
                });
            }
        } else {
            // Resetar quando selectedConversation é null
            lastLoadedSessionId.current = null;
        }
    }, [selectedConversation, loadConversation, onConversationLoaded]);

    // Limpa o chat quando selectedConversation é nullado (nova conversa)
    useEffect(() => {
        if (selectedConversation === null && hasLoadedConversation) {
            clearChat();
            setHasLoadedConversation(false);
            setHasSentInitial(false);
            lastLoadedSessionId.current = null;
        }
    }, [selectedConversation, hasLoadedConversation, clearChat]);

    // Envia mensagem inicial se fornecida
    useEffect(() => {
        if (initialMessage && !hasSentInitial && !selectedConversation) {
            setHasSentInitial(true);
            handleSendMessage(initialMessage);
        }
    }, [initialMessage, hasSentInitial, selectedConversation]);
    
    // Cleanup do debounce timer
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);
    
    // Envia mensagem (função auxiliar)
    const handleSendMessage = async (messageText) => {
        if (!messageText || isLoading) {
            return;
        }
        
        try {
            await sendMessage(messageText);
        } catch (error) {
            console.error('Erro ao enviar mensagem:', error);
        }
    };
    
    // Buscar sugestões de autocomplete
    const fetchSuggestions = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.trim().length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        
        try {
            const response = await autocompletePacientesUsuarios(searchTerm);
            const { pacientes = [], usuarios = [] } = response.data || {};
            
            const allSuggestions = [
                ...pacientes.map(p => ({ 
                    ...p, 
                    tipo: 'paciente',
                    fotoUrl: getPacienteFotoUrl(p.fotoURL)
                })),
                ...usuarios.map(u => ({ 
                    ...u, 
                    tipo: 'usuario',
                    fotoUrl: getUsuarioFotoUrl(u.fotoURL)
                }))
            ];
            
            setSuggestions(allSuggestions.slice(0, 8)); // Limitar a 8 sugestões
            setShowSuggestions(allSuggestions.length > 0);
        } catch (error) {
            console.error('Erro ao buscar sugestões:', error);
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }, []);
    
    // Detectar quando o usuário está digitando um nome
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setCursorPosition(e.target.selectionStart);
        
        // Obter posição do cursor
        const cursorPos = e.target.selectionStart;
        const textBeforeCursor = value.substring(0, cursorPos);
        
        // Encontrar a palavra atual (onde o cursor está)
        // Buscar qualquer sequência de letras (com ou sem acentos) com pelo menos 2 caracteres
        // Aceita maiúsculas, minúsculas e acentos
        const wordMatch = textBeforeCursor.match(/([a-zA-ZÁÉÍÓÚÂÊÔÇáéíóúâêôçãõÀà]{2,})$/);
        
        // Limpar timer anterior
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        
        if (wordMatch && wordMatch[0].length >= 2) {
            const searchTerm = wordMatch[0];
            // Debounce de 300ms antes de buscar
            debounceTimer.current = setTimeout(() => {
                fetchSuggestions(searchTerm);
            }, 300);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };
    
    // Inserir sugestão no input
    const insertSuggestion = (suggestion) => {
        const cursorPos = cursorPosition || inputValue.length;
        const textBeforeCursor = inputValue.substring(0, cursorPos);
        const textAfterCursor = inputValue.substring(cursorPos);
        
        // Encontrar a palavra atual para substituir
        const wordMatch = textBeforeCursor.match(/([a-zA-ZÁÉÍÓÚÂÊÔÇáéíóúâêôçãõÀà]{2,})$/);
        
        if (wordMatch) {
            const wordStart = cursorPos - wordMatch[0].length;
            const newValue = 
                inputValue.substring(0, wordStart) + 
                suggestion.nome_completo + 
                ' ' + 
                textAfterCursor;
            
            setInputValue(newValue);
            setShowSuggestions(false);
            setSuggestions([]);
            setSelectedIndex(-1);
            
            // Focar no input após inserir
            setTimeout(() => {
                inputRef.current?.focus();
                if (inputRef.current) {
                    const newCursorPos = wordStart + suggestion.nome_completo.length + 1;
                    inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                }
            }, 0);
        } else {
            // Fallback: adicionar no final
            setInputValue(inputValue + suggestion.nome_completo + ' ');
            setShowSuggestions(false);
            setSuggestions([]);
            setSelectedIndex(-1);
        }
    };
    
    // Envia mensagem do formulário
    const handleSend = async (e) => {
        e.preventDefault();
        
        if (!inputValue.trim() || isLoading) {
            return;
        }
        
        // Fechar sugestões ao enviar
        setShowSuggestions(false);
        setSuggestions([]);
        
        const message = inputValue.trim();
        setInputValue('');
        await handleSendMessage(message);
    };
    
    // Limpa o chat
    const handleClear = () => {
        if (window.confirm('Tem certeza que deseja limpar a conversa?')) {
            clearChat();
        }
    };
    
    // Handler para o textarea - apenas para controlar sugestões
    const handleTextareaFocus = () => {
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };
    
    const handleTextareaBlur = () => {
        setTimeout(() => {
            setShowSuggestions(false);
        }, 200);
    };
    
    // Classes para barra de rolagem (sempre visível)
    const scrollbarClasses = '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-400/80 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:transition-all [&::-webkit-scrollbar-thumb:hover]:bg-gray-500';
    
    return (
        <div 
            className="flex flex-col h-full chat-interface-bg"
            style={{ backgroundColor: '#ffffff', background: '#ffffff' }}
        >
            {/* Header do Chat */}
            <div 
                className="border-b border-gray-200 px-6 py-4 flex items-center justify-between !bg-white chat-interface-header"
                style={{ backgroundColor: '#ffffff', background: '#ffffff' }}
            >
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Assistente de IA</h2>
                    <p className="text-sm text-gray-500">Como posso ajudá-lo hoje?</p>
                </div>
                <div className="flex items-center space-x-2">
                    {/* Botão de Histórico */}
                    {onOpenHistory && (
                        <button
                            onClick={onOpenHistory}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Ver histórico de conversas"
                        >
                            <History className="h-5 w-5" />
                        </button>
                    )}
                    {/* Botão de Limpar */}
                    {messages.length > 0 && (
                        <button
                            onClick={handleClear}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            Limpar
                        </button>
                    )}
                </div>
            </div>
            
            {/* Área de Mensagens */}
            <div 
                className={`flex-1 overflow-y-auto px-6 py-4 scroll-smooth !bg-white chat-interface-messages ${scrollbarClasses}`}
                style={{ backgroundColor: '#ffffff', background: '#ffffff' }}
            >
                {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <div className="flex justify-center mb-4">
                                <Sparkles className="h-16 w-16 text-emerald-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-700 mb-2">
                                Olá! Sou seu assistente de IA
                            </h3>
                            <p className="text-gray-500 max-w-md">
                                Posso ajudá-lo a criar pacientes, agendar tarefas e consultas.
                                <br />
                                Como posso ajudá-lo hoje?
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {messages.map((message) => (
                            <MessageBubble
                                key={message.id}
                                role={message.role}
                                content={message.content}
                                timestamp={message.timestamp}
                                type={message.type}
                            />
                        ))}
                        {isLoading && (
                            <div className="flex justify-start my-3 transition-opacity duration-300">
                                <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                                        <span className="text-sm text-gray-500 animate-pulse">
                                            Assistente está digitando...
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>
            
            {/* Input de Mensagem */}
            <div 
                className="border-t border-gray-200 px-6 py-4 !bg-white chat-interface-input"
                style={{ backgroundColor: '#ffffff', background: '#ffffff' }}
            >
                <form onSubmit={handleSend} className="flex items-center space-x-3">
                    <div className="flex-1 relative">
                        <textarea
                            ref={inputRef}
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={(e) => {
                                // Lógica de navegação
                                if (showSuggestions && suggestions.length > 0) {
                                    if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        setSelectedIndex(prev => 
                                            prev < suggestions.length - 1 ? prev + 1 : prev
                                        );
                                    } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                                    } else if (e.key === 'Enter' && selectedIndex >= 0) {
                                        e.preventDefault();
                                        insertSuggestion(suggestions[selectedIndex]);
                                    } else if (e.key === 'Escape') {
                                        setShowSuggestions(false);
                                    } else if (e.key === 'Enter' && !e.shiftKey && selectedIndex === -1) {
                                        e.preventDefault();
                                        handleSend(e);
                                    }
                                } else if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend(e);
                                }
                            }}
                            onBlur={handleTextareaBlur}
                            onFocus={handleTextareaFocus}
                            placeholder="Digite sua mensagem..."
                            rows={1}
                            className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                            style={{ minHeight: '48px', maxHeight: '120px' }}
                            disabled={isLoading}
                        />
                        
                        {/* Dropdown de sugestões */}
                        {showSuggestions && suggestions.length > 0 && (
                            <div 
                                ref={suggestionsRef}
                                className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[200px] max-w-[300px]"
                            >
                                {suggestions.map((suggestion, index) => (
                                    <button
                                        key={`${suggestion.tipo}-${suggestion.id}`}
                                        type="button"
                                        onClick={() => insertSuggestion(suggestion)}
                                        className={`w-full text-left px-3 py-2 hover:bg-emerald-50 transition-colors flex items-center space-x-2 ${
                                            index === selectedIndex ? 'bg-emerald-100' : ''
                                        } ${index > 0 ? 'border-t border-gray-100' : ''}`}
                                    >
                                        {/* Foto do paciente/usuário */}
                                        <img
                                            src={suggestion.fotoUrl || '/images/higeia-avatar-shield.png'}
                                            alt={suggestion.nome_completo}
                                            className="w-8 h-8 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                            onError={(e) => {
                                                e.target.src = '/images/higeia-avatar-shield.png';
                                            }}
                                        />
                                        {/* Nome e role */}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-gray-900 font-medium truncate">
                                                {suggestion.nome_completo}
                                            </div>
                                            {suggestion.role && (
                                                <div className="text-xs text-gray-500 truncate">
                                                    {suggestion.role}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button
                        type="submit"
                        disabled={!inputValue.trim() || isLoading}
                        className="px-6 py-3 bg-emerald-600 text-white rounded-2xl font-medium hover:bg-emerald-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95"
                    >
                        {isLoading ? (
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Enviando...</span>
                            </div>
                        ) : (
                            'Enviar'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChatInterface;

